import React, { useMemo } from 'react';
import {
  ACTION_GROUPS,
  buildCompatibilityResultViewModel,
  evaluateCompatibilityResult,
  presentCompatibilityResultViewModel,
} from './compatibilityResultViewModel';
import './CompatibilityResult.css';

function ProductCard({ product, kicker }) {
  if (!product) return null;
  return (
    <article className="ab-compat-product-card" data-testid={`compatibility-selected-${product.slug}`}>
      {kicker || product.roleLabel ? (
        <p className="ab-compat-product-kicker">{kicker || product.roleLabel}</p>
      ) : null}
      <div className="ab-compat-product-media">
        {product.imageUrl ? (
          <img src={product.imageUrl} alt="" className="ab-compat-product-image" />
        ) : (
          <div className="ab-compat-product-placeholder" aria-hidden="true">
            {product.model || product.name}
          </div>
        )}
      </div>
      <h3 className="ab-compat-product-name">{product.name}</h3>
      <p className="ab-compat-product-meta">
        {[product.family, product.model].filter(Boolean).join(' · ')}
      </p>
    </article>
  );
}

function ActionCard({ item }) {
  return (
    <li className="ab-compat-action-card" data-testid={`compatibility-action-item-${item.slug}`}>
      <div className="ab-compat-action-card-copy">
        {item.owned ? <span className="ab-compat-owned-badge">Owned</span> : null}
        {item.roleLabel ? <p className="ab-compat-action-role">{item.roleLabel}</p> : null}
        <h4 className="ab-compat-action-name">{item.name}</h4>
        {item.explanation ? <p className="ab-compat-action-note">{item.explanation}</p> : null}
      </div>
    </li>
  );
}

function ActionGroup({ groupKey, label, items }) {
  if (!items?.length) return null;
  return (
    <section
      className={`ab-compat-action-group ab-compat-action-group-${groupKey.toLowerCase()}`}
      data-testid={`compatibility-action-${groupKey.toLowerCase()}`}
    >
      <h3 className="ab-compat-action-heading">{label}</h3>
      <ul className="ab-compat-action-list">
        {items.map((item) => (
          <ActionCard key={`${groupKey}-${item.slug}`} item={item} />
        ))}
      </ul>
    </section>
  );
}

export default function CompatibilityResult({
  build,
  catalog,
  selectedProductIds,
  ownedProductIds,
  viewModel: viewModelProp,
  includeIceCompleteness = true,
}) {
  const viewModel = useMemo(() => {
    const raw = viewModelProp
      ? viewModelProp
      : build
        ? buildCompatibilityResultViewModel(build, catalog)
        : evaluateCompatibilityResult({
          selectedProductIds,
          ownedProductIds,
          catalog,
          includeIceCompleteness,
        }).viewModel;
    return presentCompatibilityResultViewModel(raw, { includeIceCompleteness });
  }, [viewModelProp, build, catalog, selectedProductIds, ownedProductIds, includeIceCompleteness]);

  if (!viewModel) return null;

  const resultClass = `ab-compat-badge ab-compat-badge-${viewModel.resultKey.toLowerCase()}`;
  const hasActions =
    viewModel.actions.keep.length
    || viewModel.actions.add.length
    || viewModel.actions.replace.length
    || viewModel.actions.notUsed.length;

  return (
    <section className="ab-compat" data-testid="compatibility-result" data-result={viewModel.resultKey}>
      <header className="ab-compat-header">
        <p className="ab-compat-kicker">Compatibility</p>
        <div className={resultClass} data-testid="compatibility-result-badge">
          {viewModel.resultLabel}
        </div>
      </header>

      {viewModel.selectedProducts.length ? (
        <section className="ab-compat-section" data-testid="compatibility-selected">
          <h2 className="ab-compat-section-title">Selected products</h2>
          <div className="ab-compat-product-grid">
            {viewModel.selectedProducts.map((product) => (
              <ProductCard key={product.slug} product={product} />
            ))}
          </div>
        </section>
      ) : null}

      {hasActions ? (
        <section className="ab-compat-section" data-testid="compatibility-actions">
          <h2 className="ab-compat-section-title">What to do</h2>
          <div className="ab-compat-action-groups">
            <ActionGroup groupKey="keep" label={ACTION_GROUPS.KEEP} items={viewModel.actions.keep} />
            <ActionGroup groupKey="add" label={ACTION_GROUPS.ADD} items={viewModel.actions.add} />
            <ActionGroup
              groupKey="replace"
              label={ACTION_GROUPS.REPLACE}
              items={viewModel.actions.replace}
            />
            <ActionGroup
              groupKey="notused"
              label={ACTION_GROUPS.NOT_USED}
              items={viewModel.actions.notUsed}
            />
          </div>
        </section>
      ) : null}

      <section className="ab-compat-section" data-testid="compatibility-why">
        <h2 className="ab-compat-section-title">Why</h2>
        <p className="ab-compat-explanation" data-testid="compatibility-explanation">
          {viewModel.explanation}
        </p>
      </section>

      {viewModel.unresolved.length ? (
        <section className="ab-compat-section" data-testid="compatibility-unresolved">
          <h2 className="ab-compat-section-title">Still unresolved</h2>
          <ul className="ab-compat-unresolved-list">
            {viewModel.unresolved.map((item, index) => (
              <li
                key={`${item.kind}-${item.role || item.sonarSlug || index}`}
                className="ab-compat-unresolved-card"
                data-testid={`compatibility-unresolved-${item.kind}`}
              >
                <p className="ab-compat-unresolved-title">{item.title}</p>
                <p className="ab-compat-unresolved-copy">{item.explanation}</p>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </section>
  );
}
